import { collabTrackingHref } from '@/pages/collabs/suivi/collab-tracking-href';
import { formatMinorAmount } from '@/pages/collabs/ugc/ugc-tracking-format';
import { UNFINISHED_PAYOUTS_HREF } from '@/pages/payments/payouts/payouts-tab-url';
import type { Decision, DecisionDay, DecisionType } from '@fluenceur/types';
import type { TFunction } from 'i18next';

/**
 * The words and places of a decision, for the strip, its tooltip and its day
 * detail. Nothing is decided here: the server laid each decision on its day.
 */

export const K = 'decisionStrip';

/** A calendar day read as is, whatever the reader's own timezone. */
const dayDate = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

export const formatDay = (date: string, locale: string, options: Intl.DateTimeFormatOptions) =>
  dayDate(date).toLocaleDateString(locale, { ...options, timeZone: 'UTC' });

/** « 19 octobre »: a calendar day as the strip and the UGC day filter name it. */
export const formatCalendarDay = (date: string, locale: string) =>
  formatDay(date, locale, { day: 'numeric', month: 'long' });

/** « mer. 15 oct. »: the tooltip's header. */
export const formatTooltipDay = (date: string, locale: string) =>
  formatDay(date, locale, { weekday: 'short', day: 'numeric', month: 'short' });

/** How busy a day is: none, one, two, three and more decisions. */
export const levelOf = (count: number) => Math.min(count, 3);

/**
 * One shade per load, on the theme's yellow ramp (the design's #FDE68A,
 * #FACC15 and #CA8A04 map to `yellow.200`, `yellow.400` and `yellow.600`). The
 * count is written on the tile, so the shade never carries the meaning alone.
 */
export const LEVEL_COLORS = [
  { bg: 'softCream', color: 'inkMuted' },
  { bg: 'yellow.200', color: 'ink' },
  { bg: 'yellow.400', color: 'ink' },
  { bg: 'yellow.600', color: 'ink' },
] as const;

/** The Badge palette naming each type on the dashboard. */
export const TYPE_PALETTE: Record<DecisionType, string> = {
  ugc: 'lilac',
  sponsored: 'blue',
  gifting: 'orange',
  payout: 'green',
};

/** The decisions of the first three days, for the summary line. */
export const nextThreeDays = (days: DecisionDay[]) =>
  days.slice(0, 3).reduce((total, day) => total + day.decisions.length, 0);

/** « Revoir 4 contenus », « Financer 450 € », « Relancer · expire dans 2 j »: the gesture, without its day (the strip says it). */
export const gestureLabel = (t: TFunction, decision: Decision, locale: string): string => {
  const amount = formatMinorAmount(decision.amountMinorUnits, decision.currency, locale);
  switch (decision.gesture) {
    case 'review_contents':
      return t(`${K}.gesture.review_contents`, { count: decision.count });
    case 'fund':
      return amount ? t(`${K}.gesture.fund`, { amount }) : t(`${K}.gesture.fundUnquoted`);
    case 'pay_payout':
      return amount ? t(`${K}.gesture.pay_payout`, { amount }) : t(`${K}.gesture.payoutUnquoted`);
    case 'review_publications':
      return t(`${K}.gesture.review_publications`, { count: decision.count });
    case 'remind_proposal':
      return t(`${K}.gesture.remind_proposal`, { count: decision.expiresInDays ?? 0 });
    case 'resend_proposal':
      return t(`${K}.gesture.resend_proposal`);
    case 'shipment_choice':
      return t(`${K}.gesture.shipment_choice`);
    case 'reception_choice':
      return t(`${K}.gesture.reception_choice`);
    case 'unblock_gift':
      return t(`${K}.gesture.unblock_gift`);
  }
};

/** Who the decision is about: a person, or how many a payout pays. */
export const whoLabel = (t: TFunction, decision: Decision): string =>
  decision.creatorName ??
  (decision.type === 'payout' ? t(`${K}.who.affiliates`, { count: decision.count }) : '');

/** The type pill's word. */
export const typeLabel = (t: TFunction, type: DecisionType): string => t(`${K}.type.${type}`);

/** Where a decision is made: a sponsored post on Suivi, a UGC collab on the UGC page. */
export const decisionHref = (decision: Decision): string => {
  switch (decision.target.kind) {
    case 'collab':
      return decision.type === 'sponsored'
        ? collabTrackingHref(decision.target.id)
        : `/collabs/ugc?collab=${decision.target.id}`;
    case 'gift':
      return `/gifting?gift=${decision.target.id}`;
    case 'payout':
      return UNFINISHED_PAYOUTS_HREF;
  }
};
